import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  DeleteForever,
  RestartAlt,
  SmartToy,
  MenuBook,
  Business,
  Mail,
  Memory,
  RateReview,
} from "@mui/icons-material";
import apiClient from "@/api/client";
import { brand } from "@/theme";

interface PurgeAction {
  label: string;
  description: string;
  icon: React.ReactNode;
  endpoint: string;
  method: "delete" | "post";
  color: string;
  danger: "medium" | "high";
}

const purgeActions: PurgeAction[] = [
  {
    label: "Purge Messages",
    description:
      "Delete all agent-to-agent messages. Agents will generate new ones on their next heartbeat.",
    icon: <Mail />,
    endpoint: "/admin/purge/messages?confirm=true",
    method: "delete",
    color: "#1565C0",
    danger: "medium",
  },
  {
    label: "Purge Memories",
    description:
      "Delete all agent memories. Agents will rebuild memories over time through their work.",
    icon: <Memory />,
    endpoint: "/admin/purge/memory?confirm=true",
    method: "delete",
    color: "#6A1B9A",
    danger: "medium",
  },
  {
    label: "Purge Suggestions",
    description: "Delete all KB suggestions (pending and reviewed).",
    icon: <RateReview />,
    endpoint: "/admin/purge/suggestions?confirm=true",
    method: "delete",
    color: brand.warmGold,
    danger: "medium",
  },
  {
    label: "Purge Knowledge Base",
    description:
      "Delete ALL KB articles, versions, attachments, links, and suggestions. This cannot be undone.",
    icon: <MenuBook />,
    endpoint: "/admin/purge/kb?confirm=true",
    method: "delete",
    color: "#E65100",
    danger: "high",
  },
  {
    label: "Purge CRM",
    description:
      "Delete ALL CRM data — companies, individuals, communications, and follow-ups. This cannot be undone.",
    icon: <Business />,
    endpoint: "/admin/purge/crm?confirm=true",
    method: "delete",
    color: "#E65100",
    danger: "high",
  },
  {
    label: "Re-seed Agents",
    description:
      "Delete all agent data (definitions, memories, messages, config history) and re-seed the 6 character sheets from the architecture plan.",
    icon: <SmartToy />,
    endpoint: "/admin/reseed-agents?confirm=true",
    method: "post",
    color: brand.forestGreen,
    danger: "high",
  },
];

export default function DataManagement() {
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<PurgeAction | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const purgeMutation = useMutation({
    mutationFn: async (action: PurgeAction) => {
      if (action.method === "delete") {
        const res = await apiClient.delete(action.endpoint);
        return res.data;
      } else {
        const res = await apiClient.post(action.endpoint);
        return res.data;
      }
    },
    onSuccess: (_, action) => {
      queryClient.invalidateQueries();
      setConfirmAction(null);
      setSnackbar({ open: true, message: `${action.label} completed`, severity: "success" });
    },
    onError: (_, action) => {
      setSnackbar({ open: true, message: `${action.label} failed`, severity: "error" });
    },
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Data Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Purge and reset data for testing. All destructive actions require confirmation.
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        These actions permanently delete data. Download a backup first from the Backup page if you
        want to keep anything.
      </Alert>

      {/* Full reset card */}
      <Card sx={{ mb: 3, borderLeft: `4px solid #f44336` }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <RestartAlt sx={{ color: "#f44336", fontSize: 32, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">Full System Reset</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Purge everything — KB, CRM, agents, messages, memories — and re-seed the 6 agent
                character sheets. Use this to start fresh after testing.
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteForever />}
                onClick={() =>
                  setConfirmAction({
                    label: "Full System Reset",
                    description:
                      "This will DELETE ALL DATA and re-seed agents. Are you absolutely sure?",
                    icon: <RestartAlt />,
                    endpoint: "/admin/reset?confirm=true",
                    method: "post",
                    color: "#f44336",
                    danger: "high",
                  })
                }
              >
                Reset Everything
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Per-module purge */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Per-Module Purge
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {purgeActions.map((action) => (
          <Card key={action.endpoint} sx={{ borderLeft: `4px solid ${action.color}` }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ color: action.color }}>{action.icon}</Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {action.label}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                {action.description}
              </Typography>
              <Button
                variant="outlined"
                color={action.danger === "high" ? "error" : "warning"}
                size="small"
                startIcon={<DeleteForever />}
                onClick={() => setConfirmAction(action)}
              >
                {action.label}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
        <DialogTitle sx={{ color: "#f44336" }}>Confirm: {confirmAction?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction?.description ?? "This action cannot be undone. Are you sure?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmAction && purgeMutation.mutate(confirmAction)}
            disabled={purgeMutation.isPending}
          >
            {purgeMutation.isPending ? "Processing..." : "Yes, proceed"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
